import { useState } from 'react'
import type { useCollectionStatus } from '@/hooks/collections/useCollectionStatus'
import { useAddToBuylist } from '@/hooks/lists/useBuylist'

type CollectionStatus = NonNullable<ReturnType<typeof useCollectionStatus>>

/**
 * Buylist generation strategies for decks.
 * - `exact`: buy the exact printings listed in the deck.
 * - `subs`: allow alternate printings (art variants) to substitute.
 * - `cheapest`: use the cheapest available variant per base card.
 */
type BuylistMode = 'exact' | 'subs' | 'cheapest'

const BUYLIST_FIELD = {
  exact: 'buylistExact',
  subs: 'buylistWithSubs',
  cheapest: 'buylistCheapest',
} as const satisfies Record<BuylistMode, keyof CollectionStatus>

/** Adds a deck's missing cards to the buylist for the chosen mode, tracking which mode is in flight. */
export function useDeckBuylist(collectionStatus: CollectionStatus | null) {
  const addToBuylist = useAddToBuylist()
  const [addingToBuylist, setAddingToBuylist] = useState<BuylistMode | null>(null)

  const handleAddToBuylist = async (type: BuylistMode) => {
    if (!collectionStatus) return
    const items = collectionStatus[BUYLIST_FIELD[type]]
    if (items.length === 0) return
    setAddingToBuylist(type)
    try {
      for (const item of items) {
        await addToBuylist.mutateAsync({ cardId: item.cardId, quantity: item.quantity })
      }
    } finally {
      setAddingToBuylist(null)
    }
  }

  return { addingToBuylist, handleAddToBuylist }
}
